import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import styles from './comparison-cards.module.scss';
import classNames from 'classnames';
import Heading from '../Heading/Heading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

type ComparisonCardsProps = {
    className?: string;
    cards: ComparisonCardProps[];
};

export type ComparisonCardProps = {
    label: string;
    featured?: boolean;
    featuredBgColor?: string;
    content: {
        heading: string | JSX.Element;
        includes: string;
        price: string;
        list: string[];
    };
};

const ComparisonCards = ({ className, cards }: ComparisonCardsProps) => {
    const [cardHeight, setCardHeight] = useState<string>('');
    const cardStyleVars = {
        '--card-height': `${cardHeight}px`,
    } as React.CSSProperties;
    const updateSlideHeight = () => {
        setCardHeight(
            document
                .querySelector('[data-card-featured="true"]')
                ?.clientHeight.toString() || '',
        );
    };
    const pagination = {
        clickable: true,
        renderBullet: function (index: number, classNameCss: string) {
            return (
                '<span class="' +
                classNameCss +
                '">' +
                cards[index].label +
                '</span>'
            );
        },
    };
    return (
        <div className="relative z-0 mb-8" style={cardStyleVars}>
            <Swiper
                spaceBetween={0}
                slidesPerView={1.2}
                initialSlide={1}
                className={classNames(styles['comparison-cards'], className)}
                centeredSlides={true}
                centeredSlidesBounds={true}
                modules={[Pagination]}
                pagination={pagination}
                updateOnWindowResize={true}
                onResize={updateSlideHeight}
                onAfterInit={updateSlideHeight}
                breakpoints={{
                    600: {
                        slidesPerView: 1.5,
                        allowTouchMove: true,
                        touchRatio: 1,
                    },
                    992: {
                        slidesPerView: cards.length,
                        allowTouchMove: false,
                        touchRatio: 0,
                    },
                }}
            >
                {cards.map((card) => (
                    <SwiperSlide key={card.label}>
                        <div
                            className={styles['comparison-cards--card']}
                            data-card-featured={card.featured}
                        >
                            <div
                                data-card-wrapper
                                style={{
                                    backgroundColor: card.featuredBgColor,
                                }}
                            >
                                {typeof card.content.heading === 'string' ? (
                                    <Heading
                                        className={
                                            styles[
                                                'comparison-cards--card--heading'
                                            ]
                                        }
                                    >
                                        {card.content.heading}
                                    </Heading>
                                ) : (
                                    card.content.heading
                                )}
                                <p
                                    className={
                                        styles[
                                            'comparison-cards--card--includes'
                                        ]
                                    }
                                >
                                    {card.content.includes}
                                </p>
                                <p
                                    className={
                                        styles['comparison-cards--card--price']
                                    }
                                >
                                    {card.content.price}
                                </p>
                                <CardContent
                                    featured={card.featured}
                                    list={card.content.list}
                                />
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default ComparisonCards;

type CardContentProps = {
    featured?: boolean;
    list: Array<
        | {
              text: string;
          }
        | string
    >;
};

const CardContent = ({ featured, list }: CardContentProps) => {
    return (
        <div
            className={classNames(
                styles['comparison-cards--card--content'],
                featured && styles['comparison-cards--card--featured'],
            )}
        >
            <ul>
                {list.map((item, index) => {
                    return (
                        <li
                            key={index}
                            className={
                                'mb-4 flex flex-nowrap items-center last:mb-0'
                            }
                        >
                            <>
                                {featured && (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className={
                                            'mr-3 h-4 w-4 rounded-full bg-green p-1 text-green-dark'
                                        }
                                    />
                                )}
                                {typeof item === 'string' ? item : item.text}
                            </>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
